import { useEffect, useState, useCallback } from 'react';
import axios from 'common/AxiosAuth';
import { RecipientsItem, MailRecipientsSearchResponse } from '../type';

import { Grid } from 'components';
import { manageMailRecipientsGridColumns } from '../utils';
import { SelectorOutlineIcon, ChevronDownOutlineIcon } from 'assets/svgs'; 

interface Props {
  searchValue?: string;
  checkedRows?: number[];
  setCheckedRows?: React.Dispatch<React.SetStateAction<number[]>>;
  onDelete?: (selectedData: RecipientsItem[]) => void;
  onSelectionChange?: (selectedData: RecipientsItem[]) => void;
}

const ScheduleRecipientsTab = ({ searchValue, checkedRows: externalCheckedRows, setCheckedRows: externalSetCheckedRows, onDelete, onSelectionChange }: Props) => {
  const [data, setData] = useState<MailRecipientsSearchResponse | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const [internalCheckedRows, setInternalCheckedRows] = useState<number[]>([]);
  const [sortField, setSortField] = useState<string>('createdDate');
  const [sortDirection, setSortDirection] = useState<string>('DESC');

  const checkedRows = externalCheckedRows || internalCheckedRows;
  const setCheckedRows = externalSetCheckedRows || setInternalCheckedRows;

  const [visibilityMap, setVisibilityMap] = useState<Record<number, boolean>>({});

  // * Schedule Recipients 조회
  const getScheduleRecipients = useCallback(async (showLoading: boolean = true) => {
    if (showLoading) {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams();
      params.append('category', 'SCHEDULE');
      params.append('page', (currentPage - 1).toString());
      params.append('size', '10');
      params.append('sort', `${sortField},${sortDirection}`);

      if (searchValue) {
        params.append('keyword', searchValue);
      }

      // * API 호출
      const { data } = await axios.get(`/api/admin/mail/receiver/search?${params.toString()}`);

      // * API 응답
      const responseData = data?.data || {};
      const content = responseData.content || [];
      const totalPages = responseData.totalPages || 1;
      const totalElements = responseData.totalElements || 0;

      // * 페이징 정보 설정
      setTotalPages(totalPages);
      setTotalElements(totalElements);

      // * visibility map 초기화
      const initialMap = content.reduce((acc: Record<number, boolean>, item: RecipientsItem) => {
        acc[item.receiverId] = item.mailEnable;
        return acc;
      }, {} as Record<number, boolean>);
      
      setVisibilityMap(initialMap);
      setData(responseData);
    } catch (error) {
      console.error('스케줄 메일 수신자 조회 실패:', error);

      // * 에러 시 빈 데이터
      setData({
        content: [],
        totalPages: 1,
        totalElements: 0,
        pageable: {
          pageNumber: 0,
          pageSize: 10,
          sort: { empty: true, sorted: false, unsorted: true },
          offset: 0,
          paged: true,
          unpaged: false
        },
        last: true,
        size: 10,
        number: 0,
        sort: { empty: true, sorted: false, unsorted: true },
        first: true,
        numberOfElements: 0,
        empty: true
      });
      setTotalPages(1);
      setTotalElements(0);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [currentPage, searchValue, sortField, sortDirection]);

  // * 메일 수신 여부 변경 함수 
  const toggleVisibility = async (receiverId: number) => {
    const newValue = !visibilityMap[receiverId];
    
    setVisibilityMap((prev) => ({
      ...prev,
      [receiverId]: newValue,
    }));
  
    try {
      // * API 호출
      await axios.patch(`/api/admin/mail/receiver/${receiverId}`, {
        mailEnable: newValue
      });
      
    } catch (error) {
      console.error('스케줄 메일 수신 여부 변경 실패:', error);
      // * 실패 시 원복
      setVisibilityMap((prev) => ({
        ...prev,
        [receiverId]: !newValue,
      }));
    }
  };

  // * 수신자 삭제 함수
  const handleDelete = () => {
    if (onDelete && data?.content) {
      const selectedItems = checkedRows.map((rowIndex) => data.content[rowIndex]).filter(Boolean) as RecipientsItem[];
      onDelete(selectedItems);
    }
  };

  useEffect(() => {
    if (data?.content && checkedRows.length > 0) {
      const selectedItems = checkedRows.map((rowIndex) => data.content[rowIndex]).filter(Boolean) as RecipientsItem[];
      if (onSelectionChange) {
        onSelectionChange(selectedItems);
      }
    } else if (onSelectionChange) {
      onSelectionChange([]);
    }
  }, [checkedRows, onSelectionChange]);

  useEffect(() => {
    if (data?.content && checkedRows.length > 0) {
      const selectedItems = checkedRows.map((rowIndex) => data.content[rowIndex]).filter(Boolean) as RecipientsItem[];
      if (onSelectionChange) {
        onSelectionChange(selectedItems);
      }
    }
  }, [data?.content, checkedRows, onSelectionChange]);

  // * currentPage 리셋 
  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, sortField, sortDirection]);

  useEffect(() => {
    // * 초기 로딩
    const showLoading = isInitialLoad || data === null;
    getScheduleRecipients(showLoading);
  }, [getScheduleRecipients, isInitialLoad]);

  // * 정렬 변경 핸들러
  const handleSortClick = (field: string) => {
    if (sortField === field) {
      // * 같은 필드면 방향만 변경
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC');
    } else {
      // * 다른 필드면 필드 변경하고 기본값 ASC
      setSortField(field);
      setSortDirection('ASC');
    }
  };

  // * 정렬 아이콘 렌더링
  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <SelectorOutlineIcon className="[&_path]:stroke-text-tertiary h-3 w-3" />;
    }
    return (
      <ChevronDownOutlineIcon
        className={`[&_path]:stroke-text-tertiary h-3 w-3 ${sortDirection === 'ASC' ? 'rotate-180' : ''}`}
      />
    );
  };

  return (
    <Grid
      className="h-full max-w-full"
      gridColumns={manageMailRecipientsGridColumns}
      gridData={data?.content || []}
      searchValue={searchValue}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      options={{
        useFilter: false, 
        useCheckbox: true,
        useSort: {
          use: true,
          sortKeys: ['name', 'email', 'mailType', 'mailCategory', 'createdDate']
        },
        usePagination: {
          use: true,
          maxPage: totalPages
        },
        customCell: {
          mailSend: (_, row) => (
            <input
              type="checkbox"
              className="w-4 h-4 accent-primary cursor-pointer"
              checked={visibilityMap[row.receiverId] ?? true}
              onChange={() => toggleVisibility(row.receiverId)}
            />
          ),
        }
      }}
      checkedRows={checkedRows}
      setCheckedRows={setCheckedRows}
    />
  );
};

export default ScheduleRecipientsTab;
